import { EventEmitter, Injectable } from '@angular/core';
import { Observable, of, throwError, from } from 'rxjs';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Restaurant } from '../models/restaurant.model';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Role } from '../models/role.model';
import { RestaurantLocation } from '../models/restaurantLocation.model';
import { Menu } from '../models/menu.model';
import { Product } from '../models/product.model';
import { Category, mapCategoriesToServer } from '../models/category.model';
import { mapSchedulesFromServer, Schedule } from '../models/schedule.model';
import { CategoryIcon } from '../models/categoryIcon.model';
import { DomSanitizer } from '@angular/platform-browser';
import { environment as env } from '../../environments/environment.prod';
import { NbAclRole, NbAclService, NbAccessControl } from "@nebular/security";
import { environment } from '../../environments/environment';
import { Video } from '../models/video.model';
import { VideoSchedule } from '../models/videoSchedule.model';
import { Tag } from '../models/tag.model';

@Injectable({
  providedIn: 'root'
})
export class MockService {
  restaurants = `${env.apiUrl}/restaurants`;
  roles =  `${env.apiUrl}/roles`;
  locations = `${env.apiUrl}/locations`;
  menus = `${env.apiUrl}/menus`;
  products = `${env.apiUrl}/products`;
  categories = `${env.apiUrl}/categories`;
  tags = `${env.apiUrl}/tags`
  schedules = `${env.apiUrl}/schedules`;
  icons = `${env.apiUrl}/icons`;
  videos = `${env.apiUrl}/videos`;
  videoSchedules = `${env.apiUrl}/vidschedules`;

  storagePath = environment.storage;

  rst: Restaurant = new Restaurant();
  rl: Role = new Role();
  
  constructor(private http: HttpClient, private sanitizer: DomSanitizer, private aclService: NbAclService) { }
  
  // ==========================================

  getRestaurants(): Observable<Restaurant[]> {
    return this.http.get<Restaurant[]>(this.restaurants).pipe(
      map((restaurants: Restaurant[]) => {
        restaurants.forEach((restaurant) => {
          if(restaurant.logo) {
            restaurant.logo = this.storagePath + restaurant.logo;
          }
        })
        return restaurants;
      })
    );
  }

  getRestaurantById(id: string): Observable<Restaurant> {
    return this.http.get<Restaurant>(`${this.restaurants}/${id}`).pipe(
      map((restaurant)=> {
        if(restaurant.logo) {
          restaurant.logo = this.storagePath + restaurant.logo;
        }
        restaurant.schedules = mapSchedulesFromServer(restaurant.schedules);
        return restaurant;
      }),
      catchError(error => {
        if (error.status === 404) {
          return of(new Restaurant(id)); 
        } else {
          return throwError(error);
        }
      })
    );
  }

  addRestaurant( restaurant: {
    id?: string,
    name?: string,
    logo?: File
  }): Observable<Restaurant> {
    const formData = new FormData();
    formData.append("name", restaurant.name);
    if (restaurant.logo) {
      formData.append("logo", restaurant.logo, restaurant.logo.name);
    }
    return this.http.post<Restaurant>(this.restaurants, formData).pipe(
      map((restaurant) => {
        if(restaurant.logo) {
          restaurant.logo =  this.storagePath + restaurant.logo;
        }
        return restaurant;
      })
    );
  }

  updateRestaurant( restaurant: {
    id?: string,
    name?: string,
    logo?: File
  }): Observable<Restaurant> {
    const formData = new FormData();
    formData.append("name", restaurant.name);
    if (restaurant.logo) {
      formData.append("logo", restaurant.logo, restaurant.logo.name);
    }
    formData.append('_method', 'PUT');
    return this.http.post<Restaurant>(`${this.restaurants}/${restaurant.id}`, formData).pipe(
      map((restaurant) => {
        if(restaurant.logo) {
          restaurant.logo = this.storagePath + restaurant.logo;
          return restaurant;
        }
      })
    );
  }

  deleteRestaurant(id: string): Observable<{message: string, restaurant: Restaurant}> {
    return this.http.delete<{message: string, restaurant: Restaurant}>(`${this.restaurants}/${id}`);
  }

  // ==========================================
  
  getRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(this.roles);
  }
  
  getRoleByName(name: string): Observable<Role> {
    return this.http.get<Role>(`${this.roles}/${name}`);
  }
  
  addRole(role: Role): Observable<Role> {
    return this.http.post<Role>(this.roles, role);
  }
  
  updateRole(updatedRole: Role): Observable<Role> {
    return this.http.put<Role>(`${this.roles}/${updatedRole.name}`, updatedRole);
  }
  
  deleteRole(name: string): Observable<{message: string, role: Role}> {
    return this.http.delete<{message: string, role: Role}>(`${this.roles}/${name}`);
  }

  updateAccessControl(): void {
		const accessControl: NbAccessControl = {};
    this.getRoles().subscribe((rolesFromBackend: any[]) => {
      rolesFromBackend.forEach((role: any) => {
  
        const roleName = role.name;
        let roleAbilities: any;
  
        if (typeof role.abilities === 'string') {
          try {
              roleAbilities = JSON.parse(role.abilities);
          } catch (error) {
              console.error(`Error parsing role abilities for ${roleName}:`, error);
              roleAbilities = {};
          }
        } else {
            roleAbilities = role.abilities ?? {};
        }
  
        const nbAclRole: NbAclRole = {};
        Object.keys(roleAbilities).forEach((ability) => {
          nbAclRole[ability] = roleAbilities[ability];
        });
  
        accessControl[roleName] = nbAclRole;
      });
  
      this.aclService.setAccessControl(accessControl);
    })
	}
  
  // ==========================================
  
  getMenus(): Observable<Menu[]> {
    return this.http.get<Menu[]>(this.menus);
  }

  getMenuById(id: string, locationId?: string): Observable<Menu> {
    let params = new HttpParams();
    if(locationId) {
      params = params.set('locationId', locationId);
    }
    return this.http.get<Menu>(`${this.menus}/${id}`, {params}).pipe(
      map((menu: Menu) => {
        menu.products.forEach((product) => {
          if(product.photo) {
            product.photo = env.storage + product.photo;
          }
        })
        return menu;
      })
    );
  }

  addMenu(menu: Menu): Observable<Menu> {
    return this.http.post<Menu>(this.menus, menu);
  }

  updateMenu(updatedMenu: Menu): Observable<Menu> {
    return this.http.put<Menu>(`${this.menus}/${updatedMenu.id}`, {name: updatedMenu.name});
  }

  deleteMenu(id: string): Observable<{message: string, deletedMenu: Menu}> {
    return this.http.delete<{message: string, deletedMenu: Menu}>(`${this.menus}/${id}`);
  }

  setProductsInMenu(menu_id: string, item_ids: string[]): Observable<Product[]> {
    return this.http.post<Product[]>(`${this.menus}/${menu_id}/products`, {item_ids}).pipe(
      map((products: Product[]) => {
        products.forEach((product) => {
          if(product.photo) {
            product.photo = this.storagePath + product.photo;
          }
        })
        return products;
      }));
  }

  updateProductInMenu(menu_id: string, product: Product): Observable<Product> {
    return this.http.put<Product>(`${this.menus}/${menu_id}/products/${product.id}`, {price: product.price, is_available: product.is_available}).pipe(
      map((product: Product) => {
        if(product.photo) {
          product.photo = this.storagePath + product.photo;
        }
        return product;
      }));;
  }

  resetProductInMenu(product_id: string, menu_id: string): Observable<Product> {
    return this.http.put<Product>(`${this.menus}/${menu_id}/products/${product_id}`, {price: null, is_available: null}).pipe(
      map((product: Product) => {
        if(product.photo) {
          product.photo = this.storagePath + product.photo;
        }
        return product;
      }));;
  }

  // ==========================================
  
  getLocations(): Observable<RestaurantLocation[]> {
    return this.http.get<RestaurantLocation[]>(this.locations);
  }

  getLocationById(id: string): Observable<RestaurantLocation> {
    return this.http.get<RestaurantLocation>(`${this.locations}/${id}`);
  }

  addLocation(location: RestaurantLocation): Observable<RestaurantLocation> {
    return this.http.post<RestaurantLocation>(this.locations, location);
  }

  updateLocation(updatedLocation: RestaurantLocation): Observable<RestaurantLocation> {
    return this.http.put<RestaurantLocation>(`${this.locations}/${updatedLocation.id}`, updatedLocation);
  }

  deleteLocation(id: string): Observable<{message: string, location: RestaurantLocation}> {
    return this.http.delete<{message: string, location: RestaurantLocation}>(`${this.locations}/${id}`);
  }

  updateProductForLocation(product: Product, menuId: string, locationId: string): Observable<Product> {
    return this.http.put<Product>(`${this.locations}/${locationId}/menus/${menuId}/products/${product.id}`, {price: product.price, is_available: product.is_available});
  }

  resetProductForLocation(productId: string, menuId: string, locationId: string): Observable<Product> {
    return this.http.put<Product>(`${this.locations}/${locationId}/menus/${menuId}/products/${productId}`, {price: null, is_available: null});
  }

  getLocationConfig(id: string): Observable<RestaurantLocation> {
    return this.http.get<RestaurantLocation>(`${this.locations}/${id}/config`);
  }

  setLocationMenuAvailability(location_id: string, menu_id: string, active: boolean): Observable<Menu> {
    return this.http.patch<Menu>(`${this.locations}/${location_id}/menus/${menu_id}/availability`, {active});
  }


  // ==========================================
  
  getProducts(filters: {restaurantId: string, item_ids?: string[]}): Observable<Product[]> {
    let params = new HttpParams();
    params = params.set('restaurant_id', filters.restaurantId);
    if(filters.item_ids) {
      params = params.set('item_ids', filters.item_ids.toString());
    }
    return this.http.get<Product[]>(`${this.products}`, { params: params }).pipe(
      map((products: Product[]) => {
        products.forEach((product) => {
          if(product.photo) {
            product.photo = this.storagePath + product.photo;
          }
        })
        return products;
      }));
  }

  getProductById(id: string, locationId?: string): Observable<Product> {
    let params = new HttpParams();
    params = params.set('location_id', locationId);
    return this.http.get<Product>(`${this.products}/${id}`, {params}).pipe(
      map((product: Product) => {
        if(product.photo) {
          product.photo = this.storagePath + product.photo;
        }
        return product;
      }));
  }

  addProduct(product: Product): Observable<Product> {
    const formData = new FormData();
		formData.append("name", product.name);
		formData.append("description", product.description);
    formData.append("price", product.price?.toString());
    formData.append("is_available", product.is_available ? '1' : '0');
    formData.append("restaurant_id", product.restaurant_id);
 
		if (product.photo instanceof File) {
      const photo: File = product.photo as File;
			formData.append("photo", photo, photo.name);
		}
    if(product.ref_id) {
      formData.append("ref_id", product.ref_id);
    }

    mapCategoriesToServer(product.categories).then((categories) => {
			product.categories = categories;
    });

		return this.http.post<Product>(this.products, formData)
    .pipe(
      catchError((error: HttpErrorResponse) => {
        console.log(error);
        return throwError(error);
      }),
      map((product: Product) => {
        console.log(product);
          if(product && product.photo) {
            product.photo = this.storagePath + product.photo;
          }
        return product;
      })
    );
  }
  
  updateProduct(product: Product): Observable<Product> {
    const formData = new FormData();
		formData.append("name", product.name);
		formData.append("description", product.description);
    formData.append("price", product.price?.toString());
    formData.append("is_available", product.is_available ? '1' : '0');
    formData.append("restaurant_id", product.restaurant_id);
   
		if (product.photo instanceof File) {
      const photo: File = product.photo as File;
			formData.append("photo", photo, photo.name);
		}

    if(product.ref_id) {
      formData.append("ref_id", product.ref_id.toString());
    }

    formData.append('_method', 'PUT');
  
    
    mapCategoriesToServer(product.categories).then((categories) => {
			product.categories = categories;
    });
    
    return this.http.post<Product>(`${this.products}/${product.id}`, formData).pipe(
      map((product: Product) => {
        if(product.photo) {
          product.photo = this.storagePath + product.photo;
        }
        return product;
      }));;
  }

  deleteProduct(id: string): Observable<{message: string, deletedProduct: Product}> {
    return this.http.delete<{message: string, deletedProduct: Product}>(`${this.products}/${id}`);
  }

  importProducts(formData: FormData): Observable<Product> {
    return this.http.post<Product>(this.products, formData).pipe(
      map((product: Product) => {
          if(product.photo) {
            product.photo = this.storagePath + product.photo;
          }
        return product;
      }));
  }

  assignProductToCategories(menu_id: string, product_id: string, category_ids: string[]): Observable<Product> {
    return this.http.post<Product>(`${this.products}/${product_id}/assign-categories`, {menu_id, category_ids}).pipe(
      map((product: Product) => {
        if(product.photo) {
          product.photo = this.storagePath + product.photo;
        }
        return product;
      }));
  }
  // ==========================================
  
  getCategories(): Observable<Category[]> {
    return this.http.get<Category[]>(this.categories);
  }

  getCategoryById(id: string): Observable<Category> {
    return this.http.get<Category>(`${this.categories}/${id}`);
  }

  getCategoryItems(category_id: string): Observable<Product[]> {
    return this.http.get<Product[]>(`${this.categories}/${category_id}/products`).pipe(
      map((products: Product[]) => {
        products.forEach((product) => {
          if(product.photo) {
            product.photo = this.storagePath + product.photo;
          }
        })
        return products;
      }));;
  }

  setCategoryItemsOrder(category_id: string, items_order: number[]): Observable<Category> {
    return this.http.put<Category>(`${this.categories}/${category_id}/set-prod-order`, items_order);
  }

  addCategory(category: Category, menu_id: string, parent_category_id?: string): Observable<any> {
    return from(mapCategoriesToServer([category])).pipe(
      switchMap((categories) => {
        let categoryObj = categories[0];
        categoryObj.menu_id = menu_id;
        if (parent_category_id) {
          categoryObj.parent_category_id = parent_category_id;
        }
        return this.http.post<Category>(this.categories, categoryObj);
      })
    );
  }
  
  addSubCategory(subcategory: Category, parent_category_id: string, menu_id: string): Observable<any> {
    return from(mapCategoriesToServer([subcategory])).pipe(
      switchMap((categories) => {
        let subcategoryObj = categories[0];
        subcategoryObj.menu_id = menu_id;
        subcategoryObj.parent_category_id = parent_category_id;
        return this.http.post<Category>(this.categories, subcategoryObj);
      })
    );
  }
  

  updateCategory(updatedCategory: Category): Observable<Category> {
    return this.http.put<Category>(`${this.categories}/${updatedCategory.id}`,updatedCategory);
  }

  deleteCategory(id: string): Observable<{message: string, deletedCategory: Category}> {
    return this.http.delete<{message: string, deletedCategory: Category}>(`${this.categories}/${id}`);
  }

  getIcons(): Observable<CategoryIcon[]> {
    return this.http.get<{id: string; name?: string; svg: string;}[]>(this.icons).pipe(
      map((icons) => {
        return icons.map((icon) => ({
          id: icon.id.toString(),
          name: icon.name,
          svg: this.sanitizer.bypassSecurityTrustHtml(icon.svg),
        }));
      })
    );
  }

  // ==========================================
  
  newTagEmitter: EventEmitter<Tag> = new EventEmitter<Tag>();
  selectedTagEmitter: EventEmitter<Tag> = new EventEmitter<Tag>();

  getTags(restaurant_id: string): Observable<Tag[]> {
    const params = new HttpParams().set('restaurant_id', restaurant_id);
    return this.http.get<Tag[]>(this.tags, {params});
  }

  addTag(tag: Tag): Observable<Tag> {
    return this.http.post<Tag>(this.tags, {name: tag.name, restaurant_id: tag.restaurant_id});
  }

  deleteTag(id: string): Observable<{message: string, tag: Tag}> {
    return this.http.delete<{message: string, tag: Tag}>(`${this.tags}/${id}`);
  }

  updateTags(product_id: string, tag_ids: string[]): Observable<Set<Tag>> {
    return this.http.post<Set<Tag>>(`${this.products}/${product_id}/update-tags`, {tag_ids})
  }

  // ==========================================
  
  getSchedules(): Observable<Schedule[]> {
    return this.http.get<Schedule[]>(this.schedules);
  }
  
  getScheduleById(id: string): Observable<Schedule> {
    return this.http.get<Schedule>(`${this.schedules}/${id}`);
  }
  
  addSchedule(schedule: Schedule): Observable<Schedule> {
    return this.http.post<Schedule>(this.schedules, schedule);
  }
  
  updateSchedule(updatedSchedule: Schedule): Observable<Schedule> {
    return this.http.put<Schedule>(`${this.schedules}/${updatedSchedule.id}`, updatedSchedule);
  }
  
  deleteSchedule(id: string): Observable<{message: string, schedule: Schedule}> {
    return this.http.delete<{message: string, schedule: Schedule}>(`${this.schedules}/${id}`);
  }
  
  
  // ==========================================


  getVideos(restaurant_id: string): Observable<Video[]> {
    const params = new HttpParams().set('restaurant_id', restaurant_id);
    return this.http.get<{data: Video[]}>(this.videos, { params }).pipe(
      map((response) => response.data)
    );
  }
  
  getVideo(video_id: string): Observable<Video> {
    return this.http.get<Video>(`${this.videos}/${video_id}`);
  }
  
  uploadVideo(video: File, restaurant_id: string, upload_id: string): Observable<any> {
    const formData = new FormData();
    formData.append('video', video);
    formData.append('restaurant_id', restaurant_id);
    formData.append('upload_id', upload_id);
    return this.http.post<Video>(this.videos, formData, {
      reportProgress: true,
      observe: 'events',
    });
  }
  
  removeVideo(video_id: string): Observable<Video> {
    return this.http.delete<Video>(`${this.videos}/${video_id}`);
  }

  getProgress(upload_id: string) {
    const params = new HttpParams().set('upload_id', upload_id);
    return this.http.get<{progress: number}>(`${this.videos}/progress`, {params}).pipe(map((data) => data.progress))
  }
  
  // ==========================================

  getVideoSchedules(restaurant_id: string): Observable<VideoSchedule[]> {
    const params: HttpParams = new HttpParams().set('restaurant_id', restaurant_id);
    return this.http.get<VideoSchedule[]>(this.videoSchedules, {params});
  }

  getVideoSchedule(video_schedule_id: string): Observable<VideoSchedule> {
    return this.http.get<VideoSchedule>(`${this.videoSchedules}/${video_schedule_id}`);
  }

  addVideoSchedule(videoSchedule: VideoSchedule): Observable<VideoSchedule> {
    return this.http.post<VideoSchedule>(this.videoSchedules, videoSchedule);
  }

  updateVideoSchedule(videoSchedule: VideoSchedule): Observable<VideoSchedule> {
    return this.http.put<VideoSchedule>(`${this.videoSchedules}/${videoSchedule.id}`, videoSchedule);
  }

  deleteVideoSchedule(videoSchedule: VideoSchedule): Observable<VideoSchedule> {
    return this.http.delete<VideoSchedule>(`${this.videoSchedules}/${videoSchedule.id}`);
  }
}
